import { Component, computed, inject, input, linkedSignal, OnInit, output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { hasStatus } from '../../../error.utils';
import { copyToClipboard } from '../../../public/util/navigator.utils';
import { NotifyService } from '../../../service/notify.service';
import { PanoptycDataService } from '../../../service/panoptyc-data.service';
import { PosthogService } from '../../../service/posthog.service';
import { UserLoginService } from '../../../service/user-login.service';
import { LiveStreamSummary, ReportMode, SharedTransactionDetails, TimelineRow } from '../../../types';
import { FeatureFlag } from '../../../types/feature-flags';

type TheftLabel = 'Incident' | 'No incident' | 'Click to review';
@Component({
  selector: 'app-transaction-details',
  templateUrl: './transaction-details.component.html',
  styleUrls: ['./transaction-details.component.scss'],
  standalone: false,
})
export class TransactionDetailsComponent implements OnInit {
  public userService = inject(UserLoginService);
  public router = inject(Router);
  private dataService = inject(PanoptycDataService);
  private notifyService = inject(NotifyService);
  private posthogService = inject(PosthogService);

  public mode = input.required<ReportMode>();
  public cancellationId = input.required<string>();
  public transactionDetails = input<SharedTransactionDetails>();
  public isTheft = input<boolean>(false);
  public isReview = input<boolean>(false);
  public lastSeen = input<string>();
  public hasComments = input<boolean>(false);
  public hasVideo = input<boolean>(undefined);

  public isTheftInternal = linkedSignal<boolean>(() => this.isTheft());
  public lastSeenInternal = linkedSignal<string>(() => this.lastSeen());
  public hasCommentsInternal = linkedSignal<boolean>(() => this.hasComments());

  public updatingCategory = signal<boolean>(false);
  public sharedVideoLinks = signal<{ link: string; password: string }[]>([]);

  public theftLabel = computed<TheftLabel>(() => {
    const isTheft = this.isTheftInternal();
    if (isTheft === true) {
      return 'Incident';
    } else if (isTheft === false) {
      return 'No incident';
    }
    return 'Click to review';
  });

  public isDiscussionsEnabled = computed(
    () => this.posthogService.isReady() && this.posthogService.isFeatureEnabled(FeatureFlag.EnableDiscussions, false),
  );

  public hasPasswords = computed(() => this.sharedVideoLinks()?.every((v) => !!v.password));
  public videoClips = computed(() => this.transactionDetails()?.video_clips ?? []);
  public liveStreams = computed<LiveStreamSummary[]>(() => this.transactionDetails()?.live_streams ?? []);
  public hasLiveStreams = computed(() => this.liveStreams()?.length > 0 && this.sharedVideoLinks()?.length === 0);

  public timelineRows = computed<TimelineRow[]>(() => {
    const data = this.transactionDetails();
    if (!data) return [];

    const cancellationRow: TimelineRow = {
      when: 'cancellation',
      transactionId: data.image_id,
      timestamp: data.cancellation.details.cancel_date,
      items: data.cancellation.items.map((i) => `${i.description} ($${Number(i.price).toFixed(2)})`).join('\n'),
      highlight: true,
    };

    const soldRows: TimelineRow[] = (data.timeline ?? []).map((t) => ({
      when: t.occurred,
      transactionId: t.platform_id ?? String(t.transaction_id),
      timestamp: t.time_sold,
      items: t.items.map((i) => `${i.description} ($${Number(i.total).toFixed(2)})`).join('\n'),
      highlight: false,
    }));

    return [...soldRows, cancellationRow].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  });

  public misidentifiedFace = output<{ isTheft: boolean; remove: boolean }>();
  public viewed = output<{ cancellationId: string }>();
  public videoAvailable = output<{ hasVideo: boolean }>();
  public theftCategoryChange = output<{ isTheft: boolean }>();
  public newComment = output<{ newComment: boolean }>();

  url: string;
  items = [];
  date: string;

  handleDataForbidden(error: unknown) {
    if (hasStatus(error) && error.status === 403) {
      try {
        this.router.navigate(['/403']);
      } catch (e) {
        console.error(e);
      }
    }
  }

  ngOnInit() {
    this.handleCardDisplayed();
    this.dataService.getSharedVideoLinks(this.cancellationId()).subscribe(
      (data) => {
        this.sharedVideoLinks.set(data);
      },
      (err) => console.error(err),
    );
  }

  handleTheftChange(isTheft: boolean) {
    const newCategory = isTheft === true ? 1 : 0;
    this.updatingCategory.set(true);

    this.dataService.updateTransactionCategory(this.cancellationId(), newCategory).subscribe(
      () => {
        this.isTheftInternal.set(isTheft);
        this.updatingCategory.set(false);
        this.theftCategoryChange.emit({
          isTheft: isTheft,
        });
        this.notifyService.notify('Successfully updated category');
      },
      (error) => {
        this.handleDataForbidden(error);
      },
    );
  }

  handleRemoveCancellation(event: { remove: boolean }) {
    this.misidentifiedFace.emit({
      isTheft: this.isTheftInternal(),
      remove: event.remove,
    });
  }

  handleNewComment() {
    this.newComment.emit({ newComment: true });
    this.hasCommentsInternal.set(true);
  }

  handleCardDisplayed() {
    this.viewed.emit({ cancellationId: this.cancellationId() });
    // TODO: get this date from the event
    this.lastSeenInternal.set('2019-12-12 18:23:38.000000');
  }

  getDialogReturnValue() {
    return {
      lastSeen: this.lastSeenInternal(),
      isTheft: this.isTheftInternal(),
      hasVideo: this.hasVideo(),
      hasComments: this.hasCommentsInternal(),
    };
  }

  async copyToClipboard($event: MouseEvent, text: string) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }

    try {
      await copyToClipboard(text);
      this.notifyService.notify('Video password copied');
    } catch {
      this.notifyService.notify('Failed to copy the password to clipboard, please copy it manually');
    }

    if ($event) {
      return false;
    }
  }
}
